/**
 * What Vercel's install callback leaves in the address bar when it sends the customer back to ThriveStack: a
 * one-time claim id in the #fragment (#claim=…) or a failure code in the query (?vercel_error=…).
 *
 * The app redirects while it starts up (login, product selection, "overview" landing) and those redirects drop the
 * fragment, so it is captured here, synchronously at app start before any routing, removed from the address bar,
 * and parked in localStorage (shared by this browser's tabs, ignored after 2 hours 5 minutes, cleared on logout) until the
 * install page (/auth/vercel-install) has used it. It is localStorage rather than sessionStorage because a customer who has to sign in comes
 * back through an emailed magic link, which opens in a NEW tab that cannot see this tab's sessionStorage. The claim
 * id alone claims nothing: the customer still has to be signed in and, for an install that did not start from
 * ThriveStack, confirm the Vercel team shown.
 */

const STORAGE_KEY = "thrivestack_vercel_install_return";
// The claim id lives 2 hours on the server; a little longer here costs nothing because the server decides.
const MAX_AGE_MS = 125 * 60 * 1000;

export type VercelInstallReturnParams = { claimId: string | null; errorCode: string | null };

type Stored = VercelInstallReturnParams & { at: number };

/** Moves the claim id / error code from the URL into localStorage and cleans the URL. Safe to call repeatedly. */
export function captureVercelInstallReturn(): void {
    try {
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const search = new URLSearchParams(window.location.search);
        const claimId = hash.get("claim");
        // The error code is attacker-controllable (anyone can send a link) and ends up as an object key and in UI
        // logic, so only a plain lower-case code is accepted; anything else is dropped (and still stripped below).
        const rawCode = search.get("vercel_error");
        const errorCode = rawCode && /^[a-z0-9_]{1,40}$/.test(rawCode) ? rawCode : null;
        if (!claimId && !rawCode) return;
        if (!claimId && !errorCode) {
            // A malformed code: nothing worth keeping, but still clean the address bar.
            search.delete("vercel_error");
            search.delete("next");
            const q = search.toString();
            window.history.replaceState(
                window.history.state,
                "",
                `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`
            );
            return;
        }
        const entry: Stored = { claimId: claimId || null, errorCode: errorCode || null, at: Date.now() };
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
        } catch {
            /* storage unavailable: the in-memory copy below still serves this page load */
            memory = entry;
        }
        hash.delete("claim");
        search.delete("vercel_error");
        search.delete("next");
        const query = search.toString();
        const frag = hash.toString();
        window.history.replaceState(
            window.history.state,
            "",
            `${window.location.pathname}${query ? `?${query}` : ""}${frag ? `#${frag}` : ""}`
        );
    } catch {
        /* no usable location: nothing to pick up */
    }
}

let memory: Stored | null = null;

/** The captured values, if there are any and they are not stale. Does not consume them. */
export function peekVercelInstallReturn(): VercelInstallReturnParams | null {
    // Covers a page that was opened without going through app start (and a cold URL that was never captured).
    captureVercelInstallReturn();
    let entry: Stored | null = memory;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) entry = JSON.parse(raw) as Stored;
    } catch {
        /* fall back to memory */
    }
    if (!entry || typeof entry.at !== "number" || Date.now() - entry.at > MAX_AGE_MS) return null;
    if (!entry.claimId && !entry.errorCode) return null;
    return { claimId: entry.claimId ?? null, errorCode: entry.errorCode ?? null };
}

/** Forgets the captured values once they have been used (the claim finished, or the error was shown). */
export function clearVercelInstallReturn(): void {
    memory = null;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* nothing to clear */
    }
}

/** The standalone page that finishes an install that came back from Vercel (outside the app layout, like /auth/oauth-consent). */
export const VERCEL_INSTALL_PATH = "/auth/vercel-install";

/** True while an install that came back from Vercel is waiting to be claimed in this browser. */
export const hasPendingVercelInstall = (): boolean => peekVercelInstallReturn() !== null;

/** Query parameter that tells the connector to open on the Vercel step (set once the install is claimed). */
export const VERCEL_CONNECT_PARAM = "vercel";
export const VERCEL_CONNECT_VALUE = "connect";
